import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import type { InviteReturn } from '../hooks/useGmailInvite';
import { gmailInviteService, InviteSource } from '../services/gmailInviteService';

import { GmailInviteAll } from './GmailInviteAll';

import './ReferralModal.css';
import './ReferralModalTheme.css';
import './ReferralProgram.css';
import './InviteOffer.css';

/** Another window is open (sign-in's credit award, Google's return, a coupon): the offer waits until it closes. */
const otherWindowOpen = (): boolean => document.querySelector('[role="dialog"], [aria-modal="true"]') !== null;

/** Whether the server still offers it to this user, asked each time they land on the landing page. */
const useStillOffered = (userId: string | undefined, onLanding: boolean) => {
  const [offered, setOffered] = useState(false);

  useEffect(() => {
    if (!userId || !onLanding) return undefined;
    let cancelled = false;

    void gmailInviteService.status()
      .then((status) => { if (!cancelled) setOffered(Boolean(status.offer)); })
      .catch(() => undefined);

    return () => { cancelled = true; };
  }, [userId, onLanding]);

  return [offered, setOffered] as const;
};

/** Opens the offer once nothing else is open, and tells the server it was shown, so it never shows again. */
const useOpenWhenClear = (ready: boolean) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!ready) return undefined;
    const show = (): boolean => {
      if (otherWindowOpen()) return false;
      setOpen(true);
      void gmailInviteService.recordOffer('shown').catch(() => undefined);

      return true;
    };

    if (show()) return undefined;
    const watcher = new globalThis.MutationObserver(() => { if (show()) watcher.disconnect(); });

    watcher.observe(document.body, { childList: true, subtree: true });

    return () => watcher.disconnect();
  }, [ready]);

  return [open, setOpen] as const;
};

const OFFER_COPY = {
  title: 'Get 10 extra credits',
  description: 'Invite your Gmail contacts. Get 10 credits when the invites go out.',
};

interface InviteOfferModalProps {
  onClose: (cancelled: boolean) => void;
  /** Heading and line under it; defaults to the one-time 10-credit offer. */
  copy?: { title: string; description: string };
  /** Where the offer was shown, kept on the invite batches it starts. */
  source: InviteSource;
  /** Back from Google after Invite all was pressed here: why it failed, if it did. */
  notice?: string | null;
  /** Back from Google without the access Invite all needs: what to tick there, and Try again. */
  permission?: boolean;
  /** The window Google brings Invite all back to: this offer, unless its copy is not the 10-credit offer's. */
  returnTo?: InviteReturn;
}

/**
 * The offer: the Gmail row (Invite all, Every day, Choose people) and a way out. Google brings Invite all back to
 * this same window, with its progress, or with what to tick there when its permission was not given.
 */
export const InviteOfferModal: React.FC<InviteOfferModalProps> = ({
  onClose, copy = OFFER_COPY, source, notice = null, permission = false, returnTo = 'offer',
}) => {
  const [started, setStarted] = useState(false);
  const close = () => onClose(!started);

  return (
    <div className="referral-modal-overlay" onClick={close}>
      <div aria-labelledby="invite-offer-title" aria-modal="true" className="referral-modal invite-offer"
        onClick={(event) => event.stopPropagation()} role="dialog">
        <div className="referral-modal-header">
          <div className="referral-modal-heading">
            <span className="referral-modal-eyebrow">Earn credits</span>
            <h2 id="invite-offer-title">{copy.title}</h2>
            <p>{copy.description}</p>
          </div>
          <button aria-label="Close" className="referral-modal-close" onClick={close} type="button">×</button>
        </div>
        <div className="referral-modal-body">
          <div className="refer-program">
            <div className="refer-ways">
              <GmailInviteAll notice={notice} onStart={() => setStarted(true)} permission={permission}
                returnTo={returnTo} source={source} />
            </div>
          </div>
          <div className="invite-offer__actions">
            <button className="invite-offer__later" onClick={close} type="button">{started ? 'Close' : 'Not now'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * The one-time offer to invite all Gmail contacts for 10 extra credits: on the landing page only (a sign-in that goes
 * on to another page never sees it there), once per user, new or existing, who never pressed Invite all. Cancel
 * drops the Gmail access sign-in allowed; Invite all stays in Refer & earn.
 */
export const InviteOfferHost: React.FC = () => {
  const { user } = useAuth();
  const onLanding = useLocation().pathname === '/';
  const [offered, setOffered] = useStillOffered(user?.id, onLanding);
  const [open, setOpen] = useOpenWhenClear(offered && onLanding);
  const finish = (cancelled: boolean) => {
    setOpen(false);
    setOffered(false);
    if (cancelled) void gmailInviteService.recordOffer('dismissed').catch(() => undefined);
  };

  return open ? createPortal(<InviteOfferModal onClose={finish} source="signin_offer" />, document.body) : null;
};
